import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    TextField,
    IconButton,
    MenuItem,
    Menu,
} from "@mui/material";
import BaseCustomerTable from "../BaseCustomerTable/BaseCustomerTable";
import "./GroupLoansTable.scss";
import Search from "../../images/search.svg";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import pdfImage from "../../../src/images/pdf-image.svg";
import CSVImage from "../../../src/images/csv-file.png";
import LoanDetailModal from "../LoanTable/LoanDetailModal";
import LoanReportsDetailsModal from "../LoanReportsDetailsModal/LoanReportsDetailsModal";
import Arrow from "../../images/arrow-right-purple.svg";
import { useSelector } from "react-redux";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const GroupLoansTable = ({ data, columns, title = "Group Loans", formatRow, author="", handleMenuOpen, viewMenu, noOfRowsPerPage, downloads=true }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(noOfRowsPerPage || 5);
    const [sorting, setSorting] = useState({ column: "", direction: "asc" });
    const [selectedData, setSelectedData] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLoan, setSelectedLoan] = useState(null);

    let user = useSelector((state) => state.login);

    const handleSort = (columnLabel) => {
      const isAsc = sorting.column === columnLabel && sorting.direction === "asc";
      setSorting({ column: columnLabel, direction: isAsc ? "desc" : "asc" });
    };
  
    const sortedData = [...data].sort((a, b) => {
      const col = columns.find((c) => c.label === sorting.column);
      if (!col) return 0;
  
      let aVal = col.getValue(a);
      let bVal = col.getValue(b);
  
      if (!isNaN(aVal) && !isNaN(bVal)) {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (Date.parse(aVal) && Date.parse(bVal)) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
  
      const isAsc = sorting.direction === "asc";
      if (aVal < bVal) return isAsc ? -1 : 1;
      if (aVal > bVal) return isAsc ? 1 : -1;
      return 0;
    });
  
    const handleClose = () => setOpen(false);
  
    const pageCount = Math.ceil(data.length / rowsPerPage);
    const pageNumbers = Array.from({ length: pageCount }, (_, i) => i);

    const handleChangePage = (_, newPage) => {
      if(newPage != pageNumbers?.length) {
        setPage(newPage);
      }
    };
  
    useEffect(() => {
      return () => setSorting({ column: "", direction: "asc" });
    }, []);

    const filteredData = sortedData.filter((row) =>
      columns.some((col) => {
        const val = col.getValue(row);
        return val?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );

    const csvHeaders = columns.map(col => col.label);
    const csvData = [
      [title],
      [`User - ${`${user?.user?.firstName} ${user?.user?.lastName}`}`],
      [`Date Time - ${new Date().toLocaleString()}`],
      [],
      csvHeaders,
      ...data.map(row => formatRow ? formatRow(row) : columns.map(col => col.getValue(row)))
    ];

    const runPDF = () => {
      const doc = new jsPDF();
      doc.setFontSize(16);
    
      // Define base Y and line spacing
      let y = 22;
      const lineSpacing = 10;
    
      doc.text(title, 14, y); // Line 1
      y += lineSpacing;
    
      doc.setFontSize(12); // Smaller font for metadata
      doc.text(`User - ${user?.user?.firstName} ${user?.user?.lastName}`, 14, y); // Line 2
      y += lineSpacing;
    
      doc.text(`Date Time - ${new Date().toLocaleString()}`, 14, y); // Line 3
    
      const headers = [csvHeaders];
      const body = data.map(row =>
        formatRow ? formatRow(row) : columns.map(col => col.getValue(row))
      );
    
      autoTable(doc, {
        head: headers,
        body,
        startY: y + lineSpacing, // Start table below the text
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [22, 160, 133] },
      });
    
      doc.save(`${title.replace(/\s+/g, "_")}.pdf`);
    };
  
    return (
      <>
        <Box display="flex" justifyContent="space-between" >
          <Box className="loan-reports-search-container mt-4">
            <TextField
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true, 
                sx: {
                  borderRadius: '33px',
                  color: '#000',
                },
                endAdornment: (
                  <IconButton 
                    sx={{ padding: "5px", marginRight: "5px" }}
                  >
                    <img 
                      src={Search} 
                      alt="Search Icon" 
                      style={{ width: "20px", height: "20px" }} 
                      loading="lazy" 
                    />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {downloads !== false && <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
              <CSVLink data={csvData} filename={`${title.replace(/\s+/g, "_")}.csv`}>
                <IconButton>
                  <img src={CSVImage} alt="CSV" style={{ width: "40px" }} />
                </IconButton>
              </CSVLink>
              <IconButton onClick={runPDF}>
                <img src={pdfImage} alt="PDF" style={{ width: "30px" }} />
              </IconButton>
            </Box>}
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: "none" }} className="group-loans-table-container">
            <Table sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                      {columns.map((col, i) => (
                      <TableCell
                          key={i}
                          align={`${col?.align ? col.align : "left"}`}
                          className="transaction-table-headers"
                          onClick={() => handleSort(col.label)}
                      >
                          <div className={`header-cell-content ${col?.align ? "justify-content-end" : ""}`}>
                          <p className="transaction-table-headers">{col.label}</p>
                          {sorting.column === col.label && (
                              <span className="sort-arrow">
                              {sorting.direction === "asc" ? "▲" : "▼"}
                              </span>
                          )}
                          </div>
                      </TableCell>
                      ))}
                      {
                        viewMenu && <TableCell className="transaction-table-headers" align="right">
                          <div className="header-cell-content justify-content-end">
                              <p className="transaction-table-headers">Details</p>
                          </div>
                        </TableCell>
                      }
                  </TableRow>
                </TableHead>
  
                <TableBody>
                {
                    filteredData.length === 0 ? (
                    <TableRow>
                        <TableCell align="center" colSpan={columns.length + 1}>
                        <p style={{ color: "#888", padding: "16px" }}>No data available</p>
                        </TableCell>
                    </TableRow>
                    ) : (
                    filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => (
                        <TableRow key={index}>
                            {columns.map((col, i) => (
                            <TableCell key={i} align={col?.align ? col?.align : "left"}>
                                <p className="transaction-table-date">{col?.amount ? `${Number(col.getValue(row)).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : col.getValue(row) }</p>
                            </TableCell>
                            ))}
                            {
                              viewMenu && <TableCell align="right">
                                <div
                                    className="d-flex justify-content-end align-items-center details-container"
                                    style={{ cursor: "pointer" }}
                                >
                                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </div>
                              </TableCell>
                            }
                        </TableRow>
                        ))
                    )
                }
                </TableBody>
            </Table>
  
            <BaseCustomerTable
                page={page}
                onPageChange={handleChangePage}
                pageNumbers={pageNumbers}
            />
        </TableContainer>
      </>
    );
};

export default GroupLoansTable;